import {
  DASHBOARD_ACCOUNT_COLUMN_STORAGE_KEY,
  DASHBOARD_ACCOUNT_COLUMNS,
  DASHBOARD_BUYER_COLUMN_STORAGE_KEY,
  DASHBOARD_BUYER_COLUMNS,
  DASHBOARD_FIRST_COL_ACCOUNT,
  DASHBOARD_FIRST_COL_BUYER,
  loadColumnOrder,
} from "../lib/columns"
import {
  computePresetRange,
  type DashboardDatePreset,
} from "../lib/presets"

/**
 * Reducer for the Dashboard page filter + pagination state. Field
 * names intentionally mirror the legacy globals in `dashboard.html`
 * so QA can cross-reference the parity file.
 *
 * State shape rules:
 *   - `activeTab` + `network` + `status` + date fields persist across
 *     tab switches.
 *   - Search text is CLEARED on tab switch (parity: line 1458).
 *   - Pagination resets to 1 on tab switch, any filter change, or a
 *     sort key change.
 *   - Default sort is `cost` desc for both tables. When clicking a
 *     different column, text columns switch to `asc` first, all
 *     others switch to `desc` first.
 *   - `buyerColumnOrder` / `accountColumnOrder` are initialised from
 *     `localStorage` via `loadColumnOrder` on mount.
 */

export type DashboardTab = "buyer" | "account"

export interface DashboardFilterState {
  activeTab: DashboardTab
  selectedNetworks: string[]
  /** Campaign status filters for the by-buyer tab (ENABLE/DISABLE/DELETE). */
  selectedBuyerStatuses: string[]
  /** Spend-based activity filters for the by-account tab. */
  selectedAccountStatuses: string[]
  /** Selected buyer user_ids for client-side row filtering (empty = show all). */
  selectedBuyers: number[]
  datePreset: DashboardDatePreset
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  dateDisplayLabel: string
  search: string
  buyerPage: number
  accountPage: number
  buyerSortKey: string
  buyerSortDir: "asc" | "desc"
  accountSortKey: string
  accountSortDir: "asc" | "desc"
  buyerColumnOrder: string[]
  accountColumnOrder: string[]
}

export type DashboardAction =
  | { type: "SET_TAB"; tab: DashboardTab }
  | { type: "SET_NETWORKS"; values: string[] }
  | { type: "SET_BUYER_STATUSES"; values: string[] }
  | { type: "SET_ACCOUNT_STATUSES"; values: string[] }
  | { type: "SET_BUYERS"; ids: number[] }
  | {
      type: "SET_PRESET"
      preset: DashboardDatePreset
      startDate: string
      endDate: string
      displayLabel: string
    }
  | {
      type: "SET_CUSTOM_RANGE"
      startDate: string
      endDate: string
      displayLabel: string
    }
  | { type: "SET_SEARCH"; value: string }
  | { type: "SET_BUYER_SORT"; key: string; columnType: "string" | "number" }
  | { type: "SET_ACCOUNT_SORT"; key: string; columnType: "string" | "number" }
  | { type: "SET_BUYER_PAGE"; page: number }
  | { type: "SET_ACCOUNT_PAGE"; page: number }
  | { type: "SET_BUYER_COLUMN_ORDER"; order: string[] }
  | { type: "SET_ACCOUNT_COLUMN_ORDER"; order: string[] }

/** Lazy initialiser — computes today's range and reads localStorage once. */
export function createInitialDashboardState(): DashboardFilterState {
  const today = computePresetRange("today")
  return {
    activeTab: "buyer",
    selectedNetworks: [],
    selectedBuyerStatuses: [],
    selectedAccountStatuses: ["WITH_SPEND"],
    selectedBuyers: [],
    datePreset: "today",
    startDate: today.startDate,
    endDate: today.endDate,
    dateDisplayLabel: today.displayLabel,
    search: "",
    buyerPage: 1,
    accountPage: 1,
    buyerSortKey: "cost",
    buyerSortDir: "desc",
    accountSortKey: "cost",
    accountSortDir: "desc",
    buyerColumnOrder: loadColumnOrder(
      DASHBOARD_BUYER_COLUMN_STORAGE_KEY,
      DASHBOARD_BUYER_COLUMNS,
      DASHBOARD_FIRST_COL_BUYER,
    ),
    accountColumnOrder: loadColumnOrder(
      DASHBOARD_ACCOUNT_COLUMN_STORAGE_KEY,
      DASHBOARD_ACCOUNT_COLUMNS,
      DASHBOARD_FIRST_COL_ACCOUNT,
    ),
  }
}

export function dashboardReducer(
  state: DashboardFilterState,
  action: DashboardAction,
): DashboardFilterState {
  switch (action.type) {
    case "SET_TAB":
      if (action.tab === state.activeTab) return state
      // Parity: clear search, reset both page counters on tab
      // switch. Network/status/date survive (line 1442-1463).
      return {
        ...state,
        activeTab: action.tab,
        search: "",
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_NETWORKS":
      return {
        ...state,
        selectedNetworks: action.values,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_BUYER_STATUSES":
      return {
        ...state,
        selectedBuyerStatuses: action.values,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_ACCOUNT_STATUSES":
      return {
        ...state,
        selectedAccountStatuses: action.values,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_BUYERS":
      return {
        ...state,
        selectedBuyers: action.ids,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_PRESET":
      return {
        ...state,
        datePreset: action.preset,
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_CUSTOM_RANGE":
      return {
        ...state,
        datePreset: "custom",
        startDate: action.startDate,
        endDate: action.endDate,
        dateDisplayLabel: action.displayLabel,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_SEARCH":
      return {
        ...state,
        search: action.value,
        buyerPage: 1,
        accountPage: 1,
      }
    case "SET_BUYER_SORT": {
      if (state.buyerSortKey === action.key) {
        return {
          ...state,
          buyerSortDir: state.buyerSortDir === "asc" ? "desc" : "asc",
          buyerPage: 1,
        }
      }
      return {
        ...state,
        buyerSortKey: action.key,
        buyerSortDir: action.columnType === "string" ? "asc" : "desc",
        buyerPage: 1,
      }
    }
    case "SET_ACCOUNT_SORT": {
      if (state.accountSortKey === action.key) {
        return {
          ...state,
          accountSortDir: state.accountSortDir === "asc" ? "desc" : "asc",
          accountPage: 1,
        }
      }
      return {
        ...state,
        accountSortKey: action.key,
        accountSortDir: action.columnType === "string" ? "asc" : "desc",
        accountPage: 1,
      }
    }
    case "SET_BUYER_PAGE":
      return { ...state, buyerPage: action.page }
    case "SET_ACCOUNT_PAGE":
      return { ...state, accountPage: action.page }
    case "SET_BUYER_COLUMN_ORDER":
      return { ...state, buyerColumnOrder: action.order }
    case "SET_ACCOUNT_COLUMN_ORDER":
      return { ...state, accountColumnOrder: action.order }
    default:
      return state
  }
}
